import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    LinearProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {getBuildModeLabel} from 'utils/buildMode';

const statusColors = {
    pending: 'warning',
    running: 'info',
};

const statusLabels = {
    pending: '대기 중',
    running: '빌드 중',
};

export default function ActiveBuilds({builds, queue, onViewBuild, onCancelBuild}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const activeBuilds = builds.filter(b => b.status === 'pending' || b.status === 'running');

    if (activeBuilds.length === 0) {
        return null;
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const renderMobileCard = (build) => (
        <Box
            key={build.id}
            sx={{
                p: 2, mb: 1.5, bgcolor: 'background.default',
                borderRadius: 1, border: 1, borderColor: 'divider',
            }}
        >
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                <Chip
                    label={statusLabels[build.status] || build.status}
                    color={statusColors[build.status] || 'default'}
                    size="small"
                />
                <Box sx={{display: 'flex', gap: 1}}>
                    <Button size="small" variant="outlined" onClick={() => onViewBuild(build.id)}>
                        로그
                    </Button>
                    {(build.status === 'pending' || build.status === 'running') && (
                        <Button size="small" variant="outlined" color="error" onClick={() => onCancelBuild(build.id)}>
                            취소
                        </Button>
                    )}
                </Box>
            </Box>
            <Typography sx={{fontSize: '0.875rem', fontWeight: 600}}>
                {build.productName || '-'}
            </Typography>
            <Typography sx={{fontSize: '0.8125rem', color: 'text.secondary'}}>
                {build.version || build.tag || '-'}
                {build.profile ? ` · ${build.profile}` : ''}
                {` · ${getBuildModeLabel(build.mode)}`}
            </Typography>
            <Typography sx={{fontSize: '0.75rem', color: 'text.secondary', mt: 0.5}}>
                {build.requestedBy} · {formatDate(build.createdAt)}
            </Typography>
        </Box>
    );

    return (
        <Paper sx={{p: {xs: 2, md: 3}, mb: 3}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                <Typography variant="h2">
                    활성 빌드 ({activeBuilds.length})
                </Typography>
                {queue && (
                    <Typography sx={{fontSize: '0.875rem', color: 'text.secondary'}}>
                        대기: {queue.queueLength}개 | 진행: {queue.isProcessing ? '1개' : '없음'}
                    </Typography>
                )}
            </Box>

            {isMobile ? (
                <Box>{activeBuilds.map(renderMobileCard)}</Box>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>상태</TableCell>
                                <TableCell>제품</TableCell>
                                <TableCell>릴리즈</TableCell>
                                <TableCell>프로파일</TableCell>
                                <TableCell>모드</TableCell>
                                <TableCell>요청자</TableCell>
                                <TableCell>요청 시간</TableCell>
                                <TableCell>액션</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {activeBuilds.map((build) => (
                                <TableRow key={build.id}>
                                    <TableCell>
                                        <Chip
                                            label={statusLabels[build.status] || build.status}
                                            color={statusColors[build.status] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{build.productName || '-'}</TableCell>
                                    <TableCell>{build.version || build.tag || '-'}</TableCell>
                                    <TableCell>{build.profile || '-'}</TableCell>
                                    <TableCell>{getBuildModeLabel(build.mode)}</TableCell>
                                    <TableCell>{build.requestedBy}</TableCell>
                                    <TableCell>{formatDate(build.createdAt)}</TableCell>
                                    <TableCell>
                                        <Box sx={{display: 'flex', gap: 1}}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => onViewBuild(build.id)}
                                            >
                                                로그
                                            </Button>
                                            {(build.status === 'pending' || build.status === 'running') && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => onCancelBuild(build.id)}
                                                >
                                                    취소
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {activeBuilds.some(b => b.status === 'running') && (
                <LinearProgress sx={{mt: 2}}/>
            )}
        </Paper>
    );
}